// Stage-specific transition schemas. Single source of truth — used by both
// the React forms in src/components/admin/transitions/* and the API validator
// at functions/api/admin/_lib/validators.js (mirrored there for backend parity).
//
// Per item 1.5 (§3.5 of the comprehensive plan). Each schema lists the fields
// captured in `interactions.structured_payload_json` when the transition lands.

export const CHANNEL_OPTIONS = ['phone', 'email', 'walk_in', 'referral'];

export const ENGAGEMENT_RESPONSE_TYPES = ['interested', 'considering', 'gatekept'];

export const VERBAL_YES_CONFIRMED_BY = [
  'principal', 'assistant_principal', 'superintendent', 'purchase_secretary', 'other',
];

export const DORMANT_REASONS = [
  'no_response', 'not_interested_now', 'budget', 'lost_to_competitor', 'other',
];

export const CLOSED_LOST_REASONS = [...DORMANT_REASONS];

export const BILLING_CADENCES = ['monthly', 'quarterly', 'biannual', 'annual'];

export const SUPPORT_TIERS = ['standard', 'priority', 'enterprise'];

// Each schema entry:
//   fields: array of { key, label, type, required, options? }
//     type ∈ 'text' | 'textarea' | 'date' | 'select' | 'number' | 'currency' | 'checkbox'
//   summaryFromPayload(payload): function → string used as the auto-logged
//     interaction's `summary` field if the form doesn't supply one explicitly.
export const TRANSITION_SCHEMAS = Object.freeze({
  contacted: {
    fields: [
      { key: 'channel', label: 'Channel', type: 'select', options: CHANNEL_OPTIONS, required: true },
      { key: 'contact_person_name', label: 'Contact person', type: 'text', required: true },
      { key: 'summary', label: 'Brief summary', type: 'textarea', required: true },
    ],
    summaryFromPayload: (p) => p?.summary || `Contacted via ${p?.channel || 'unknown'}`,
  },

  engaged: {
    fields: [
      { key: 'response_type', label: 'Response type', type: 'select', options: ENGAGEMENT_RESPONSE_TYPES, required: true },
      { key: 'next_action', label: 'Next action', type: 'text', required: true },
      { key: 'next_action_date', label: 'Next action date', type: 'date', required: false },
      { key: 'summary', label: 'Notes', type: 'textarea', required: true },
    ],
    summaryFromPayload: (p) => p?.summary || `Engaged — ${p?.next_action || 'next steps TBD'}`,
  },

  verbal_yes: {
    fields: [
      { key: 'confirmed_by', label: 'Confirmed by', type: 'select', options: VERBAL_YES_CONFIRMED_BY, required: true },
      { key: 'expected_contract_signing_date', label: 'Expected contract signing date', type: 'date', required: false },
      { key: 'verbal_conditions', label: 'Verbal conditions (optional)', type: 'textarea', required: false },
      { key: 'create_draft_invoice', label: 'Create draft invoice now', type: 'checkbox', required: false },
      { key: 'summary', label: 'Summary', type: 'textarea', required: true },
    ],
    summaryFromPayload: (p) => p?.summary || `Verbal yes from ${p?.confirmed_by || 'contact'}`,
  },

  contracted: {
    fields: [
      { key: 'sow_signed_date', label: 'SOW signed date', type: 'date', required: true },
      { key: 'final_contract_value_cents', label: 'Final contract value', type: 'currency', required: true },
      { key: 'po_number', label: 'PO number (optional)', type: 'text', required: false },
      { key: 'first_invoice_due_date', label: 'First invoice due date', type: 'date', required: false },
      { key: 'summary', label: 'Summary', type: 'textarea', required: true },
    ],
    summaryFromPayload: (p) => p?.summary || `Contracted — SOW signed ${p?.sow_signed_date || ''}`,
  },

  dormant: {
    fields: [
      { key: 'reason', label: 'Reason', type: 'select', options: DORMANT_REASONS, required: true },
      { key: 'revive_after_date', label: 'Revive after (optional)', type: 'date', required: false },
      { key: 'summary', label: 'Notes', type: 'textarea', required: false },
    ],
    summaryFromPayload: (p) => p?.summary || `Dormant — ${p?.reason || 'no_response'}`,
  },

  closed_lost: {
    fields: [
      { key: 'reason', label: 'Reason', type: 'select', options: CLOSED_LOST_REASONS, required: true },
      { key: 'lessons_learned', label: 'Lessons learned', type: 'textarea', required: false },
      { key: 'summary', label: 'Notes', type: 'textarea', required: false },
    ],
    summaryFromPayload: (p) => p?.summary || `Closed lost — ${p?.reason || 'no_response'}`,
  },

  // Captures the start of revenue-retention. The dates + cadence drive the
  // maintenance-renewals widget on the financial dashboard, so getting these
  // structured (vs. burying them in free text) is what makes that widget useful.
  maintained: {
    fields: [
      { key: 'maintenance_start_date', label: 'Maintenance start date', type: 'date', required: true },
      { key: 'billing_cadence', label: 'Billing cadence', type: 'select', options: BILLING_CADENCES, required: true },
      { key: 'support_tier', label: 'Support tier', type: 'select', options: SUPPORT_TIERS, required: false },
      { key: 'account_manager', label: 'Account manager', type: 'text', required: false },
      { key: 'contract_end_date', label: 'Contract end date (optional)', type: 'date', required: false },
      { key: 'summary', label: 'Notes', type: 'textarea', required: false },
    ],
    summaryFromPayload: (p) => p?.summary || `Maintained — ${p?.billing_cadence || 'cadence TBD'} from ${p?.maintenance_start_date || 'TBD'}`,
  },

  // Used for backward moves (dormant → prospect, closed_lost → contacted via
  // prospect re-entry). Captures *why* a dead account is being revived so the
  // pipeline funnel doesn't accumulate ghost re-entries.
  prospect: {
    fields: [
      { key: 'restart_reason', label: 'Restart reason', type: 'text', required: true },
      { key: 'expected_re_engagement_date', label: 'Expected re-engagement date', type: 'date', required: false },
      { key: 'summary', label: 'Notes', type: 'textarea', required: false },
    ],
    summaryFromPayload: (p) => p?.summary || `Prospect restart — ${p?.restart_reason || 'no reason given'}`,
  },
});

export function transitionSchema(stage) {
  return TRANSITION_SCHEMAS[stage] || { fields: [] };
}

// Field-level validation. Returns null on pass, or an object map of
// { fieldKey: errorMessage } on fail. Used identically on UI and backend.
export function validateTransitionPayload(stage, payload) {
  const schema = TRANSITION_SCHEMAS[stage];
  if (!schema) return null;
  const errors = {};
  for (const field of schema.fields) {
    const v = payload?.[field.key];
    if (field.required) {
      if (v == null || v === '' || (typeof v === 'string' && v.trim() === '')) {
        errors[field.key] = `${field.label} is required`;
        continue;
      }
    }
    if (field.type === 'select' && v != null && v !== '' && !field.options.includes(v)) {
      errors[field.key] = `${field.label} must be one of: ${field.options.join(', ')}`;
    }
    if (field.type === 'date' && v != null && v !== '') {
      // Accept "YYYY-MM-DD". Anything else is rejected at boundary.
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(v))) {
        errors[field.key] = `${field.label} must be a YYYY-MM-DD date`;
      }
    }
    if (field.type === 'currency' && v != null && v !== '') {
      if (!Number.isFinite(Number(v)) || Number(v) < 0) {
        errors[field.key] = `${field.label} must be a non-negative number`;
      }
    }
    if (field.type === 'number' && v != null && v !== '') {
      if (!Number.isFinite(Number(v))) {
        errors[field.key] = `${field.label} must be a number`;
      }
    }
  }
  return Object.keys(errors).length ? errors : null;
}
